import jwtDecode from 'jwt-decode';
import get from 'lodash/get';

import { ExperimentName } from '@letsdothis/experiments';
import { digestCookies } from '@letsdothis/quarantine';

import getUrl from '@/routes/routes/helpers/getUrl';
import { getObjFromHashParamString } from '@/utils/urls';
import { logError } from '@/utils/logError';
import {
  MAX_REFRESH_TIME,
  COGNITO_LAST_RETURN_URL,
  USER_COOKIE_NAME as LEGACY_USER_COOKIE_NAME,
} from './constants';
import { isSuperUserFromJWT } from './utils';

import oldAuth, { initAuth as legacyInitAuth } from './auth';

export const initAuth = async (client, cookies) => {
  const experimentsFromCookies = digestCookies(cookies.getAll());
  if (experimentsFromCookies[ExperimentName.LDT_LOGIN] !== 'variant') {
    return legacyInitAuth();
  }
  // idToken and primaryIdentityId will be mutated by the
  // handleCallback and logout ONLY, this is needed until all the auth conusmer
  // are async
  let idToken = await client.getIdToken();
  let primaryIdentityId = await client.getPrimaryIdentityId();
  const authService = { loginHandlers: [] };

  authService.addLoginHandler = (handler) => {
    const { loginHandlers } = authService;

    if (!loginHandlers.includes(handler)) {
      loginHandlers.push(handler);
    }
  };

  authService.removeLoginHandler = (handler) => {
    authService.loginHandlers = authService.loginHandlers.filter(
      (h) => h !== handler,
    );
  };

  authService.handleLoginStateChange = () => {
    authService.loginHandlers.forEach((h) => {
      h(authService.loggedIn());
    });
  };

  authService.shouldUseLegacyAuth = () => {
    if (experimentsFromCookies[ExperimentName.LDT_LOGIN] === 'variant') {
      return false;
    }
    return true;
  };

  authService.redirectToLegacyAuthIfStrava = (isLoginExperimentVariant) => {
    if (__CLIENT__) {
      const legacyPrimaryIdentityId = cookies.get(LEGACY_USER_COOKIE_NAME);
      if (
        legacyPrimaryIdentityId &&
        legacyPrimaryIdentityId.includes('strava')
      ) {
        if (isLoginExperimentVariant) {
          const currentUrl = window.location.href;
          const hash = currentUrl.split('#')[1];
          const hashObject = hash ? getObjFromHashParamString(hash) : {};
          const query = {
            exp_disable: ExperimentName.LDT_LOGIN,
          };
          const url = getUrl.loginPage({
            query,
            hash: hashObject,
          });
          window.location.href = url;
        }
      }
    }
  };

  // this function will mutate the cookies per request
  // horrible, but the lesser evil for now
  authService.setCookiesDomain = (domain) => {
    client.setCookiesDomain(domain);
  };

  authService.getUserCookie = () => {
    if (authService.shouldUseLegacyAuth()) {
      return oldAuth.getUserCookie();
    }
    return primaryIdentityId;
  };

  authService.getPrimaryIdentityId = () => {
    if (authService.shouldUseLegacyAuth()) {
      return oldAuth.getUserCookie();
    }
    return primaryIdentityId;
  };

  authService.getAuthCookie = () => {
    if (authService.shouldUseLegacyAuth()) {
      return oldAuth.getAuthCookie();
    }
    return idToken;
  };

  authService.getIdToken = () => {
    if (authService.shouldUseLegacyAuth()) {
      return oldAuth.getAuthCookie();
    }
    return idToken;
  };

  authService.updateIdToken = (newIdToken) => {
    idToken = newIdToken;
  };

  authService.updatePrimaryIdentityId = (newIdToken) => {
    idToken = newIdToken;
  };

  authService.setCookie = (name, value) => {
    cookies.set(name, value, {
      domain: get(window, 'location.hostname', ''),
      expires: new Date(new Date().getTime() + MAX_REFRESH_TIME),
      path: '/', // Required to be used site-wide
      secure: !__DEVELOPMENT__,
    });
  };

  authService.login = async (auth) => {
    if (authService.shouldUseLegacyAuth()) {
      await oldAuth.login(auth);
      authService.handleLoginStateChange();

      return;
    }
    const { provider, returnUrl } = auth;
    if (!provider) return;
    authService.setCookie(COGNITO_LAST_RETURN_URL, returnUrl);
    await client.loginProvider(provider);
    authService.handleLoginStateChange();
  };

  authService.loginEmailPassword = async ({ returnUrl, email, password }) => {
    if (!email || !password) return;
    authService.setCookie(COGNITO_LAST_RETURN_URL, returnUrl);
    await client.loginUsernamePassword({
      username: email,
      password,
    });
    authService.handleLoginStateChange();
  };

  authService.signUpEmailPassword = async ({
    email,
    givenName,
    familyName,
    password,
  }) => {
    if (!email || !password || !givenName || !familyName) return;
    await client.signUpUsernamePassword({
      username: email,
      password,
      email,
      givenName,
      familyName,
    });
  };

  authService.recoverPassword = async ({ email }) => {
    await client.recoverPassword({ email });
  };

  authService.confirmForgotPassword = async ({
    confirmationCode,
    email,
    password,
  }) => {
    await client.confirmForgotPassword({ confirmationCode, email, password });
  };

  authService.refresh = async () => {
    await client.refresh();
    const newIdToken = await client.getIdToken();
    authService.updateIdToken(newIdToken);
  };

  authService.getCookie = (name) => cookies.get(name);

  authService.removeCookie = (name) => {
    cookies.remove(name, {
      domain: get(window, 'location.hostname', ''),
      path: '/', // Required to be used site-wide
    });
  };

  authService.popCookie = (name) => {
    const value = authService.getCookie(name);
    authService.removeCookie(name);
    return value;
  };

  authService.getReturnUrl = () => {
    return authService.popCookie(COGNITO_LAST_RETURN_URL) ?? '/';
  };

  authService.handleCallback = async (code) => {
    await client.handleCallback(code);
    idToken = await client.getIdToken();
    primaryIdentityId = await client.getPrimaryIdentityId();
    authService.handleLoginStateChange();
  };

  authService.logout = (manual) => {
    if (oldAuth.loggedIn()) {
      oldAuth.logout(manual);
    }

    if (client.isLoggedIn()) {
      client.logout();
    }

    idToken = null;
    primaryIdentityId = null;
    authService.handleLoginStateChange();
  };

  authService.hasUserCookie = () => {
    if (authService.shouldUseLegacyAuth()) {
      return oldAuth.hasUserCookie();
    }
    return client.isLoggedIn();
  };

  authService.loggedIn = () => {
    if (authService.shouldUseLegacyAuth()) {
      return oldAuth.loggedIn();
    }
    return client.isLoggedIn();
  };

  authService.authenticateRoute = (props, replace) => {
    if (authService.shouldUseLegacyAuth()) {
      return oldAuth.authenticateRoute(props, replace);
    }
    const {
      location: { pathname, hash, search },
    } = props;
    if (!client.isLoggedIn()) {
      replace({
        pathname: '/login',
        query: { returnUrl: `${pathname}${search}${hash}` },
      });
    }
  };

  /**
   * Handler called when entering admin routes from Router
   * Decodes the auth token and checks that the user roles contain 'admin'.
   * Redirects to logout if the user is not an admin.
   * Copies old functionality
   */
  authService.adminRoute = (props, replace) => {
    if (authService.shouldUseLegacyAuth()) {
      return oldAuth.adminRoute(props, replace);
    }
    let admin = false;
    try {
      const authCookie = authService.getAuthCookie();
      const user = jwtDecode(authCookie);
      if (user && isSuperUserFromJWT(user)) {
        admin = true;
      }
    } catch (error) {
      // JWT could not be decoded, or cookie doesn't exist
      logError(error);
    }
    if (!admin) {
      // TODO: This should probably 404 with no sign that there is an admin route here
      // To logout the user is intentionally annoying, but may also be annoying for dev/testing.
      replace({
        pathname: '/logout',
      });
    }
  };

  return authService;
};
